import React from 'react';
import {
  type AttachmentElement,
  type ComponentProps,
  type EditorPlugin,
} from '../../../types/aha-editor';
import ToolDownload from '../tools/ToolDownload';
import ToolDelete from '../../../tools/ToolDelete';
import {
  ToolAttachmentDisplayLink,
  ToolEmbedDisplayIconLeft,
  ToolEmbedDisplayIconAbove,
} from '../tools/ToolAttachmentDisplay';
import ToolEditFileName from '../tools/ToolEditFileName';
import { isAttachmentElement } from '../../../types/guards';
import styles from './AttachmentComponentPlugin.module.css';
import classNames from 'classnames';
import SelectionRectangle from '../../../components/SelectionRectangle/SelectionRectangle';
import MediaLoader from './MediaLoader';

const mimeTypeToIconMapping: Record<string, string> = {
  // Generic media types.
  image: 'fa-file-image',
  audio: 'fa-file-audio',
  video: 'fa-file-video',

  // Applications
  'application/pdf': 'fa-file-pdf',
  'application/msword': 'fa-file-word',
  'application/vnd.ms-word': 'fa-file-word',
  'application/vnd.oasis.opendocument.text': 'fa-file-word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'fa-file-word',
  'application/vnd.ms-excel': 'fa-file-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    'fa-file-excel',
  'application/vnd.oasis.opendocument.spreadsheet': 'fa-file-excel',
  'application/vnd.ms-powerpoint': 'fa-file-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'fa-file-powerpoint',
  'application/vnd.oasis.opendocument.presentation': 'fa-file-powerpoint',
  'text/plain': 'fa-file-text',
  'text/html': 'fa-file-code',
  'application/json': 'fa-file-code',
  'application/gzip': 'fa-file-archive',
  'application/zip': 'fa-file-archive',
};

const mimeTypeToIcon = (mimeType: string): string => {
  const prefix = mimeType.split('/')[0];
  if (mimeTypeToIconMapping.hasOwnProperty(mimeType)) {
    return mimeTypeToIconMapping[mimeType];
  } else if (mimeTypeToIconMapping.hasOwnProperty(prefix)) {
    return mimeTypeToIconMapping[prefix];
  } else {
    return 'fa-file';
  }
};

// Render an attachment element.
const Attachment: React.FC<ComponentProps<AttachmentElement>> = ({
  element,
  attributes,
  children,
}) => {
  if (!isAttachmentElement(element)) return null;

  let {
    attachment_display: display,
    attachment_fileName: fileName,
    attachment_mimeType: mimeType,
  } = element;

  display ||= 'icon-left';

  return (
    <SelectionRectangle inline={true} hasMargin={false} hasBackgroundFill>
      <span
        {...attributes}
        className={classNames(styles.attachment, {
          [styles.iconLeft]: display === 'icon-left',
          [styles.iconAbove]: display === 'icon-above',
        })}
      >
        <MediaLoader element={element} inline />
        {['icon-left', 'icon-above'].includes(display) && (
          <i
            className={classNames(
              styles.icon,
              'far',
              mimeTypeToIcon(mimeType ?? '')
            )}
          />
        )}
        <a href={element.media_src} className={styles.name}>
          {fileName}
        </a>
        {children}
      </span>
    </SelectionRectangle>
  );
};

const AttachmentComponentPlugin: EditorPlugin = {
  name: 'Attachment component plugin',
  component: {
    type: 'attachment',
    component: Attachment,
    toolbarItems: [
      [ToolDownload],
      [ToolEditFileName],
      [
        ToolAttachmentDisplayLink,
        ToolEmbedDisplayIconLeft,
        ToolEmbedDisplayIconAbove,
      ],
      [ToolDelete],
    ],
  },
};

export default AttachmentComponentPlugin;
